import { useEffect, useRef } from "react";
import "./NextConsoleAnnouncementBanner.css";

export const NEXT_CONSOLE_ANNOUNCEMENT_BANNER_DEFAULT_HEIGHT = 45;
export const NEXT_CONSOLE_ANNOUNCEMENT_DISMISSED_IDS_KEY =
  "nextConsoleAnnouncementBanner.dismissedIds";

// Bump this ID whenever the message changes so the banner re-appears for
// users who dismissed a previous version.
export const NEXT_CONSOLE_ANNOUNCEMENT_MESSAGE_ID =
  "2026-08-retirement-august-3-new-ui-blog";

const ANNOUNCEMENT_MESSAGE =
  "The new Mondoo is here — try it out before this version retires on August 3rd.";
const NEW_MONDOO_ORIGIN = "https://app.mondoo.com";
const LEARN_MORE_HREF =
  "https://mondoo.com/blog/mondoo-new-ui-vulnerability-management";

function getDismissedIds(): string[] {
  try {
    const raw = localStorage.getItem(
      NEXT_CONSOLE_ANNOUNCEMENT_DISMISSED_IDS_KEY,
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

export function isNextConsoleAnnouncementDismissed(): boolean {
  return getDismissedIds().includes(NEXT_CONSOLE_ANNOUNCEMENT_MESSAGE_ID);
}

// The banner advertises the new Mondoo UI, which is not yet available to
// private-instance customers (e.g. mondoo.companyname.com). Suppress it unless
// the current hostname is mondoo.com, a subdomain of mondoo.com, or
// localhost (so local dev still sees it).
export function isMondooHostedInstance(): boolean {
  if (typeof window === "undefined") return true;
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === "mondoo.com" ||
    hostname.endsWith(".mondoo.com") ||
    hostname === "localhost"
  );
}

function getNewMondooHref(): string {
  if (typeof window === "undefined") return NEW_MONDOO_ORIGIN;
  const { pathname, search, hash } = window.location;
  return `${NEW_MONDOO_ORIGIN}${pathname}${search}${hash}`;
}

type Props = {
  top?: number;
  onDismiss: () => void;
  onHeightChange?: (height: number) => void;
};

export default function NextConsoleAnnouncementBanner({
  top = 0,
  onDismiss,
  onHeightChange,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || !onHeightChange) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [onHeightChange]);

  const handleClose = () => {
    const dismissed = new Set(getDismissedIds());
    dismissed.add(NEXT_CONSOLE_ANNOUNCEMENT_MESSAGE_ID);
    try {
      localStorage.setItem(
        NEXT_CONSOLE_ANNOUNCEMENT_DISMISSED_IDS_KEY,
        JSON.stringify(Array.from(dismissed)),
      );
    } catch {
      // Ignore storage failures (private mode, quota, etc.) — the banner
      // will simply re-appear on next load.
    }
    onDismiss();
  };

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Announcement"
      className="a-announcement-bar"
      style={{ position: "fixed", top, left: 0, width: "100%", zIndex: 1300 }}
    >
      <div className="a-announcement-bar__inner">
        <span className="a-announcement-bar__spacer" aria-hidden="true" />
        <p className="a-announcement-bar__message type-body">
          {ANNOUNCEMENT_MESSAGE}
          <a
            href={getNewMondooHref()}
            target="_blank"
            rel="noopener noreferrer"
            className="a-announcement-bar__link"
            aria-label="Open new Mondoo (opens in new tab)"
          >
            Open new Mondoo
          </a>
          <a
            href={LEARN_MORE_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="a-announcement-bar__link"
            aria-label="Read blog post about the new Mondoo UI (opens in new tab)"
          >
            Learn more
          </a>
        </p>
        <button
          type="button"
          onClick={handleClose}
          className="a-announcement-bar__dismiss"
          aria-label="Dismiss announcement"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
